import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle2, Circle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { useProductAuth } from '@orisini/ui/context/AuthContext'
import { api } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi } from '@/features/accounts/api'
import { boardsApi } from '@/features/boards/api'
import { slotsApi } from '@/features/slots/api'

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  )
}

interface Setup {
  accounts: number
  accountsMax: number
  devices: number
  devicesConnected: number
  boards: number
  slots: number
}

/**
 * Ringkasan kesiapan akun, dipakai untuk checklist onboarding.
 *
 * Board & slot diperiksa pada akun PERTAMA saja: yang ingin dijawab checklist
 * ini adalah "sudah bisa mulai posting atau belum", bukan audit tiap akun.
 */
async function muatSetup(): Promise<Setup> {
  const [akun, perangkat] = await Promise.all([
    accountsApi.list(),
    api
      .get<{ devices: Array<{ connected: boolean }> }>('/agent/devices')
      .catch(() => ({ devices: [] })),
  ])

  const pertama = akun.accounts[0]
  let boards = 0
  let slots = 0
  if (pertama) {
    const [b, s] = await Promise.all([
      boardsApi.list(pertama.id).catch(() => null),
      slotsApi.list(pertama.id).catch(() => null),
    ])
    boards = b?.boards.length ?? 0
    slots = s?.slots.length ?? 0
  }

  return {
    accounts: akun.accounts.length,
    accountsMax: akun.limit.max,
    devices: perangkat.devices.length,
    devicesConnected: perangkat.devices.filter((d) => d.connected).length,
    boards,
    slots,
  }
}

function LangkahBaris({
  selesai,
  judul,
  keterangan,
  to,
  aksi,
}: {
  selesai: boolean
  judul: string
  keterangan: string
  to: string
  aksi: string
}) {
  return (
    <li className="flex items-start gap-3">
      {selesai ? (
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
      ) : (
        <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      )}
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm', selesai ? 'text-muted-foreground line-through' : 'font-medium')}>
          {judul}
        </p>
        <p className="text-xs text-muted-foreground">{keterangan}</p>
      </div>
      {!selesai && (
        <Link
          to={to}
          className="flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
        >
          {aksi}
          <ArrowRight className="size-3" />
        </Link>
      )}
    </li>
  )
}

export default function DashboardPage() {
  const { me } = useProductAuth()
  const [setup, setSetup] = useState<Setup | null>(null)

  useEffect(() => {
    let aktif = true
    muatSetup()
      .then((s) => aktif && setSetup(s))
      .catch(() => undefined)
    return () => {
      aktif = false
    }
  }, [])

  if (!me) return null

  const langkah = setup && [
    {
      selesai: setup.devices > 0,
      judul: 'Install the Pinboss app on your computer',
      keterangan:
        setup.devices > 0
          ? `${setup.devices} devices installed · ${setup.devicesConnected} connected`
          : 'Posting is done by the desktop app, not the server',
      to: '/agent',
      aksi: 'Download',
    },
    {
      selesai: setup.accounts > 0,
      judul: 'Add a Pinterest account',
      keterangan:
        setup.accounts > 0
          ? `${setup.accounts} connected accounts`
          : 'Via the "Add Pinterest account" button in the desktop app',
      to: '/accounts',
      aksi: 'How to add',
    },
    {
      selesai: setup.boards > 0,
      judul: 'Sync boards',
      keterangan:
        setup.boards > 0 ? `${setup.boards} boards available` : 'A board is required for every pin',
      to: '/boards',
      aksi: 'Sync',
    },
    {
      selesai: setup.slots > 0,
      judul: 'Set up time slots',
      keterangan:
        setup.slots > 0 ? `${setup.slots} slots per week` : 'Weekly posting hours for the queue',
      to: '/slots',
      aksi: 'Set up',
    },
    {
      selesai: me.usage.scheduled > 0 || me.usage.posted > 0,
      judul: 'Add pins to the queue',
      keterangan: 'One at a time from the Pins page, or many at once via Bulk input',
      to: '/pins/bulk',
      aksi: 'Start',
    },
  ]

  const sisaLangkah = langkah?.filter((l) => !l.selesai).length ?? 0

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Hi, {me.user.name || me.user.email.split('@')[0]}
        </h1>
        <p className="text-sm text-muted-foreground">
          Plan {me.plan.name} · period {me.usage.periodYm}
        </p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Published pins" value={me.usage.posted} hint="this month" />
        <Stat label="Pins in queue" value={me.usage.scheduled} hint="scheduled & waiting" />
        <Stat
          label="Remaining quota"
          value={me.usage.remaining}
          hint={`of ${me.plan.limits.maxPostsPerMonth} per month, all platforms`}
        />
        <Stat
          label="Pinterest accounts"
          value={setup ? `${setup.accounts} / ${setup.accountsMax}` : '…'}
          hint="connected"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{sisaLangkah > 0 ? 'Next steps' : 'All set'}</CardTitle>
          <CardDescription>
            {sisaLangkah > 0
              ? `${sisaLangkah} steps left before pins can publish automatically`
              : 'Setup complete — leave the computer on and the agent connected.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {langkah ? (
            <ul className="space-y-3">
              {langkah.map((l) => (
                <LangkahBaris key={l.judul} {...l} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
